import axios from "axios";

/**
	* @typedef {"CLOSE" | "OPEN" | "BREAK" | "VACATION"} ScheduleStatus
	* @typedef {"RECORDING" | "TTS" | "FILE"} VoicemailType
	* @typedef {"fr-CA" | "fr-FR" | "en-CA" | "en-US"} SystemLocale
	* @typedef {Partial<Record<SystemLocale, string>>} Translation
	*/

/**
	* @typedef {Object} VoicemailFileView
	* @property {string} [name]
	* @property {string} [url]
	*/

/**
	* @typedef {Object} VoicemailView
	* @property {string} id
	* @property {ScheduleStatus} state
	* @property {string} organizationId
	* @property {string} [userGroupId]
	* @property {boolean} disabled
	* @property {VoicemailType} type
	* @property {VoicemailFileView} [file]
	* @property {Translation} [text] - Localised TTS strings keyed by locale
	* @property {string} [url]
	* @property {Translation} [speechUrl] - Localised TTS preview URLs keyed by locale
	* @property {string} [version]
	*/

const vacationV2VoicemailService = {
	/**
		* Get all vacation v2 voicemails for the current organization
		* @returns {Promise<VoicemailView[]>}
		*/
	async getAll() {
		const { data } = await axios.get("voicemails/vacations/v2");

		return data;
	},

	/**
		* Get a single voicemail by ID
		* @param {string} id
		* @returns {Promise<VoicemailView>}
		*/
	async getById(id) {
		const { data } = await axios.get(`voicemails/vacations/v2/${id}`);

		return data;
	},

	/**
		* Get multiple voicemails by a list of IDs
		* @param {string[]} ids
		* @returns {Promise<VoicemailView[]>}
		*/
	async getManyByIds(ids) {
		const { data } = await axios.post("voicemails/vacations/v2/bulk", { ids });

		return data;
	},

	/**
		* Create a new voicemail using TTS (text-to-speech).
		* `text` is a Translation map keyed by locale; pass any subset of locales
		* you want to synthesise (empty/missing keys are simply skipped server-side).
		* @param {{
		*   text?: Translation,
		*   type: VoicemailType,
		*   state: ScheduleStatus,
		*   url?: string,
		*   disabled?: boolean,
		*   userGroupId?: string
		* }} payload
		* @returns {Promise<VoicemailView>}
		*/
	async createGenerated(payload) {
		const { data } = await axios.post("voicemails/vacations/v2", payload);

		return data;
	},

	/**
		* Upload a file-based voicemail draft
		* @param {string} id - Voicemail ID
		* @param {File} file - Audio file
		* @param {boolean} disabled - Whether the voicemail is disabled
		* @returns {Promise<VoicemailView>}
		*/
	async uploadFile(file, disabled = false) {
		const formData = new FormData();

		formData.append("file", file);
		formData.append("disabled", String(disabled));

		const { data } = await axios.patch("voicemails/vacations/v2/file", formData, {
			headers: {
				"Content-Type": "multipart/form-data"
			}
		});

		return data;
	},

	/**
		* Delete a voicemail by ID
		* @param {string} id
		* @returns {Promise<void>}
		*/
	async remove(id) {
		await axios.delete(`voicemails/vacations/v2/${id}`);
	}
};

export default vacationV2VoicemailService;
